import React, { FormEvent } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Button,
  IconButton,
  Typography,
  Alert,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import * as S from "../style";

export interface EditUserForm {
  full_name: string;
  role: string;
  language: string;
}

interface EditUserDialogProps {
  open: boolean;
  formData: EditUserForm;
  formError: string | null;
  onClose: () => void;
  onFormChange: (data: EditUserForm) => void;
  onSubmit: (e: FormEvent) => void;
}

const ROLE_OPTIONS = [
  { value: "user", label: "User" },
  { value: "clinician", label: "Clinician" },
  { value: "superadmin", label: "Superadmin" },
];

const LANGUAGE_OPTIONS = [
  { value: "en", label: "🇺🇸 English" },
  { value: "no", label: "🇳🇴 Norwegian" },
];

const EditUserDialog: React.FC<EditUserDialogProps> = ({
  open,
  formData,
  formError,
  onClose,
  onFormChange,
  onSubmit,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{ sx: S.StyledDialogPaper }}
  >
    <DialogTitle sx={S.StyledDialogTitle}>
      <Typography variant="h6" fontWeight={600}>
        Edit User
      </Typography>
      <IconButton size="small" onClick={onClose}>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <form onSubmit={onSubmit}>
      <DialogContent dividers>
        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}
        <Stack {...S.StyledFormStack}>
          <TextField
            label="Full Name"
            type="text"
            fullWidth
            value={formData.full_name}
            onChange={(e) =>
              onFormChange({ ...formData, full_name: e.target.value })
            }
          />
          <TextField
            select
            label="Role"
            fullWidth
            value={formData.role}
            onChange={(e) =>
              onFormChange({ ...formData, role: e.target.value })
            }
          >
            {ROLE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Language"
            required
            fullWidth
            value={formData.language}
            helperText="The single language this user's app will be shown in"
            onChange={(e) =>
              onFormChange({ ...formData, language: e.target.value })
            }
          >
            {LANGUAGE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions sx={S.StyledDialogActions}>
        <Button
          variant="ghost"
          onClick={onClose}
          sx={{
            color: 'text.primary',
            borderColor: 'divider',
          }}
        >
          Cancel
        </Button>
        <Button type="submit" variant="contained">
          Save
        </Button>
      </DialogActions>
    </form>
  </Dialog>
);

export default EditUserDialog;
